import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, User, Star, ChevronDown, ChevronUp, Briefcase } from 'lucide-react';
import { PerformanceReview } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function PerformanceReviewsPage() {
  const [reviews, setReviews] = useState<PerformanceReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [expandedReview, setExpandedReview] = useState<number | null>(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/performance-reviews`);
      if (!response.ok) {
        throw new Error('Failed to fetch performance reviews');
      }
      const data = await response.json();
      setReviews(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating?: string) => {
    switch (rating) {
      case 'SUPERB': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'STRONGLY_EXCEEDS_EXPECTATIONS': return 'bg-green-100 text-green-800 border-green-200';
      case 'SIGNIFICANT_IMPACT':
      case 'EXCEEDS_EXPECTATIONS': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CONSISTENTLY_MEETS_EXPECTATIONS': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'NEEDS_IMPROVEMENT': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRatingIcon = (rating?: string) => {
    switch (rating) {
        case 'SUPERB': return <Star className="h-4 w-4 text-purple-600" />;
        case 'STRONGLY_EXCEEDS_EXPECTATIONS': return <ChevronUp className="h-4 w-4 text-green-600" />;
        case 'SIGNIFICANT_IMPACT':
        case 'EXCEEDS_EXPECTATIONS': return <TrendingUp className="h-4 w-4 text-blue-600" />;
        case 'CONSISTENTLY_MEETS_EXPECTATIONS': return <User className="h-4 w-4 text-gray-600" />;
        case 'NEEDS_IMPROVEMENT': return <ChevronDown className="h-4 w-4 text-red-600" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const filteredReviews = selectedYear 
    ? reviews.filter(review => new Date(review.review_date).getFullYear() === selectedYear)
    : reviews;

  const years = Array.from(new Set(reviews.map(review => new Date(review.review_date).getFullYear()))).sort((a, b) => b - a);

  const toggleExpanded = (reviewId: number) => {
    setExpandedReview(expandedReview === reviewId ? null : reviewId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading performance reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Reviews</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchReviews}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Performance Reviews</h1>
                <p className="text-sm text-gray-600">Track your performance over time</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <div className="text-sm text-gray-500">
                {filteredReviews.length} reviews
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Timeline */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {filteredReviews.map((review) => {
            const isExpanded = expandedReview === review.review_id;
            
            return (
              <div key={review.review_id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpanded(review.review_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 relative">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRatingColor(review.rating)}`}>
                          {getRatingIcon(review.rating)}
                        </div>
                        {review.is_promoted ? (
                          <div className="absolute -bottom-1 -right-1 bg-blue-600 rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
                            <span className="text-white font-bold text-xs">P</span>
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {review.review_period}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <User className="h-4 w-4 mr-1" />
                            {review.employee_name || 'Unknown Employee'}
                          </span>
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(review.review_date).toLocaleDateString()}
                          </span>
                          {review.role_title && (
                            <span className="flex items-center">
                              <Briefcase className="h-4 w-4 mr-1" />
                              {review.role_title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(review.rating)}`}>
                        {review.rating}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Role Information */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Role Information</h4>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-gray-600">Role:</span> {review.role_title || 'Unknown'}
                          </div>
                          {review.job_level && (
                            <div>
                              <span className="text-gray-600">Level:</span> L{review.job_level}
                            </div>
                          )}
                          {review.job_family && (
                            <div>
                              <span className="text-gray-600">Family:</span> {review.job_family}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Review Information */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Review Information</h4>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-gray-600">Employee:</span> {review.employee_name || 'Unknown'}
                          </div>
                          <div>
                            <span className="text-gray-600">Period:</span> {review.review_period}
                          </div>
                          <div>
                            <span className="text-gray-600">Date:</span> {new Date(review.review_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feedback */}
                    {review.feedback && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Feedback & Details</h4>
                        <div
                          className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg prose"
                          dangerouslySetInnerHTML={{ __html: review.feedback }}
                        />
                      </div>
                    )}
                    
                    {/* Promotion Information */}
                    {review.is_promoted !== undefined && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Promotion Status</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            review.is_promoted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {review.is_promoted ? 'Promoted' : 'Not Promoted'}
                          </span>
                          {review.rating === 'CONSISTENTLY_MEETS_EXPECTATIONS' && (
                            <span className="text-sm text-gray-600">
                              (Note: CME is a standard rating immediately following a promotion.)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredReviews.length === 0 && (
          <div className="text-center py-12">
            <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No reviews found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedYear 
                ? `No performance reviews found for ${selectedYear}.`
                : 'No performance reviews have been recorded yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Performance Summary */}
      {reviews.length > 0 && (
        <div className="bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{reviews.length}</div>
                <div className="text-sm text-gray-500">Total Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {reviews.filter(r => ['SUPERB', 'STRONGLY_EXCEEDS_EXPECTATIONS', 'SIGNIFICANT_IMPACT', 'EXCEEDS_EXPECTATIONS'].includes(r.rating || '')).length}
                </div>
                <div className="text-sm text-gray-500">High Performance Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {Math.round((reviews.filter(r => ['SUPERB', 'STRONGLY_EXCEEDS_EXPECTATIONS', 'SIGNIFICANT_IMPACT', 'EXCEEDS_EXPECTATIONS'].includes(r.rating || '')).length / reviews.length) * 100)}%
                </div>
                <div className="text-sm text-gray-500">High Performance Rate</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
